import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Input, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { AlertOutlined, CalendarOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import { useTmmdeEquipmentList, useTmmdeKpis } from '@/hooks/useCalibration';
import type { TmmdeEquipmentFilters, TmmdeEquipmentRow, TmmdeStatus } from '@/services/calibration.service';
import { formatDate } from '@/utils/format/date';
import {
  CALIBRATION_KIND_OPTIONS,
  OWNERSHIP_OPTIONS,
  TMMDE_STATUS_META,
  TMMDE_STATUS_ORDER,
  isCompanyOwned,
  optionLabel,
  tmmdeStatusColor,
  tmmdeStatusLabel,
} from './tmmdeDisplay';

const { Title, Text } = Typography;

function unwrapArray<T>(res: unknown): T[] {
  const ax = (res as { data?: { data?: T[] } })?.data;
  return Array.isArray(ax) ? ax : Array.isArray(ax?.data) ? (ax as { data: T[] }).data : [];
}

const DUE_WINDOWS = [
  { value: 0, label: 'Due today or overdue' },
  { value: 7, label: 'Due within 7 days' },
  { value: 30, label: 'Due within 30 days' },
  { value: 60, label: 'Due within 60 days' },
  { value: 90, label: 'Due within 90 days' },
];

const TmmdeEquipmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canCreateSchedule = Boolean(
    user?.is_super_admin || user?.permissions?.includes('calibration.schedule.create')
  );
  const canViewImpact = Boolean(
    user?.is_super_admin ||
      user?.permissions?.some((p) => p === 'calibration.impact.view' || p === 'calibration.impact.manage')
  );
  const { data: kpis } = useTmmdeKpis();
  const [filters, setFilters] = useState<TmmdeEquipmentFilters>({});
  const [q, setQ] = useState('');
  const { data, isLoading, refetch, isFetching } = useTmmdeEquipmentList({ ...filters, q: q || undefined });
  const rows = data?.rows ?? [];
  const summary = data?.summary ?? { total: 0, by_status: {} };

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses-tmmde'],
    queryFn: async () => {
      try {
        return unwrapArray<{ id: string; warehouse_name: string }>(await api.get('/inventory/warehouses'));
      } catch {
        return [];
      }
    },
    staleTime: 10 * 60 * 1000,
  });

  const setStatus = (status?: TmmdeStatus) =>
    setFilters((f) => ({ ...f, status: f.status === status ? undefined : status }));

  const columns: ColumnsType<TmmdeEquipmentRow> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'asset_code',
        width: 130,
        fixed: 'left',
        render: (v: string, r) => (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/tmmde/${r.id}`)}>
            {v}
          </Button>
        ),
      },
      {
        title: 'Equipment',
        dataIndex: 'asset_name',
        render: (v: string, r) => (
          <Space direction="vertical" size={0}>
            <span>{v}</span>
            {(r.manufacturer || r.model) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[r.manufacturer, r.model].filter(Boolean).join(' · ')}
              </Text>
            )}
          </Space>
        ),
      },
      { title: 'S/N', dataIndex: 'serial_number', width: 130, render: (v) => v || '—' },
      {
        title: 'Range / accuracy',
        key: 'range',
        width: 170,
        render: (_: unknown, r) =>
          [r.working_range, r.accuracy && `± ${r.accuracy}`, r.measurement_unit].filter(Boolean).join(' · ') || '—',
      },
      {
        title: 'Location',
        key: 'location',
        width: 150,
        render: (_: unknown, r) => r.warehouse_name || [r.site, r.building, r.room].filter(Boolean).join(' / ') || '—',
      },
      {
        title: 'Ownership',
        dataIndex: 'equipment_ownership',
        width: 130,
        render: (v: string) =>
          isCompanyOwned(v) ? optionLabel(OWNERSHIP_OPTIONS, v) : <Tag color="purple">{optionLabel(OWNERSHIP_OPTIONS, v)}</Tag>,
      },
      {
        title: 'Type / interval',
        key: 'interval',
        width: 150,
        render: (_: unknown, r) =>
          r.schedule_id ? (
            <span>
              {optionLabel(CALIBRATION_KIND_OPTIONS, r.calibration_kind)}
              {r.calibration_frequency_days ? ` · ${r.calibration_frequency_days} d` : ''}
              {r.interval_basis === 'FIRST_USE' ? ' (first use)' : ''}
            </span>
          ) : (
            <Text type="warning">No schedule</Text>
          ),
      },
      {
        title: 'Calibrated by',
        key: 'by',
        width: 150,
        render: (_: unknown, r) => r.supplier_name || r.calibration_provider || r.calibration_type || '—',
      },
      {
        title: 'Last calibration',
        dataIndex: 'last_calibration_date',
        width: 120,
        render: (v) => formatDate(v, { nullDisplay: '—' }),
      },
      {
        title: 'Due date',
        dataIndex: 'calibration_due_date',
        width: 140,
        sorter: (a, b) => (a.days_to_due ?? 99999) - (b.days_to_due ?? 99999),
        render: (v, r) => {
          if (!v) return r.interval_basis === 'FIRST_USE' ? <Text type="secondary">On first use</Text> : '—';
          const d = Number(r.days_to_due);
          const hint = d < 0 ? `${Math.abs(d)} d overdue` : d === 0 ? 'today' : `in ${d} d`;
          return (
            <Space direction="vertical" size={0}>
              <span>{formatDate(v)}</span>
              <Text type={d < 0 ? 'danger' : d <= 30 ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
                {hint}
              </Text>
            </Space>
          );
        },
      },
      {
        title: (
          <Tooltip title="Failed calibrations / calibrations in the review window">Failures</Tooltip>
        ),
        key: 'failures',
        width: 100,
        render: (_: unknown, r) => {
          const total = Number(r.record_count_window || 0);
          const failed = Number(r.fail_count_window || 0);
          if (!total) return '—';
          return <Text type={failed >= 2 ? 'danger' : failed ? 'warning' : undefined}>{`${failed} / ${total}`}</Text>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'calibration_status',
        width: 150,
        fixed: 'right',
        render: (v: string, r) => (
          <Tooltip title={r.not_for_use_reason || undefined}>
            <Tag color={tmmdeStatusColor(v)}>{tmmdeStatusLabel(v)}</Tag>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
        <Title level={2} style={{ margin: 0 }}>
          Equipment List
        </Title>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => void refetch()} loading={isFetching}>
            Refresh
          </Button>
          <Button icon={<CalendarOutlined />} onClick={() => navigate('/grc/calibration')}>
            Calibration register
          </Button>
          {canViewImpact && (
            <Button icon={<AlertOutlined />} onClick={() => navigate('/qms/tmmde/impact')}>
              Impact assessments
            </Button>
          )}
          {canCreateSchedule && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/grc/calibration/schedules/new')}>
              Add equipment to calibration
            </Button>
          )}
        </Space>
      </Space>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Testing, measuring, monitoring and detection equipment (TMMDE). Equipment comes from the asset register — flag
        the asset as measuring equipment or create a calibration schedule for it to appear here.
      </Text>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} lg={3}>
          <Card size="small" hoverable onClick={() => setFilters((f) => ({ ...f, status: undefined }))}>
            <Statistic title="Total" value={summary.total} />
          </Card>
        </Col>
        {TMMDE_STATUS_ORDER.map((s) => (
          <Col xs={12} sm={8} lg={3} key={s}>
            <Card
              size="small"
              hoverable
              onClick={() => setStatus(s)}
              style={filters.status === s ? { borderColor: '#1677ff', boxShadow: '0 0 0 1px #1677ff' } : undefined}
            >
              <Statistic
                title={TMMDE_STATUS_META[s].label}
                value={summary.by_status[s] ?? 0}
                valueStyle={
                  s === 'OVERDUE' || s === 'NOT_FOR_USE'
                    ? { color: '#cf1322' }
                    : s === 'DUE_SOON' || s === 'QUARANTINED'
                      ? { color: '#d46b08' }
                      : s === 'CALIBRATED'
                        ? { color: '#389e0d' }
                        : undefined
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      {kpis && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title={
                  <Tooltip title="Calibrations in the last 12 months done on or before the previous due date">
                    Calibrated on time (12 months)
                  </Tooltip>
                }
                value={kpis.on_time_percent ?? '—'}
                suffix={kpis.on_time_percent != null ? '%' : undefined}
                valueStyle={
                  kpis.on_time_percent == null
                    ? undefined
                    : { color: kpis.on_time_percent >= 95 ? '#389e0d' : kpis.on_time_percent >= 80 ? '#d46b08' : '#cf1322' }
                }
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Based on {kpis.on_time_basis} recalibration(s)
              </Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Failure rate (12 months)"
                value={kpis.failure_rate_percent ?? '—'}
                suffix={kpis.failure_rate_percent != null ? '%' : undefined}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {kpis.calibrations_last_12_months} calibration(s)
              </Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Overdue now" value={kpis.overdue} valueStyle={kpis.overdue ? { color: '#cf1322' } : undefined} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card
              size="small"
              hoverable={canViewImpact}
              onClick={canViewImpact ? () => navigate('/qms/tmmde/impact') : undefined}
            >
              <Statistic
                title="Open impact assessments"
                value={kpis.open_impact_assessments}
                valueStyle={kpis.open_impact_assessments ? { color: '#d46b08' } : undefined}
              />
            </Card>
          </Col>
        </Row>
      )}

      {(kpis?.interval_review.length ?? 0) > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Review calibration interval"
          description={
            <Space direction="vertical" size={0}>
              <span>Repeated calibration failures suggest these intervals are too long:</span>
              {kpis!.interval_review.map((r) => (
                <Button
                  key={r.asset_id}
                  type="link"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => navigate(`/qms/tmmde/${r.asset_id}`)}
                >
                  {r.asset_code} — {r.asset_name}: {r.failures} failures in {r.window_months} months
                  {r.calibration_frequency_days ? ` (current interval ${r.calibration_frequency_days} days)` : ''}
                </Button>
              ))}
            </Space>
          }
        />
      )}

      {(summary.by_status.OVERDUE ?? 0) > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${summary.by_status.OVERDUE} equipment overdue for calibration`}
          description="Overdue equipment must not be used for inspection, test or monitoring until recalibrated and accepted by Quality."
        />
      )}

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            placeholder="Search ID, name, S/N, manufacturer"
            style={{ width: 280 }}
            onSearch={setQ}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={TMMDE_STATUS_ORDER.map((s) => ({ value: s, label: TMMDE_STATUS_META[s].label }))}
          />
          <Select
            allowClear
            placeholder="Ownership"
            style={{ width: 200 }}
            value={filters.ownership}
            onChange={(v) => setFilters((f) => ({ ...f, ownership: v }))}
            options={OWNERSHIP_OPTIONS}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Location"
            style={{ width: 200 }}
            value={filters.warehouse_id}
            onChange={(v) => setFilters((f) => ({ ...f, warehouse_id: v }))}
            options={warehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))}
          />
          <Select
            allowClear
            placeholder="Due window"
            style={{ width: 190 }}
            value={filters.due_within_days}
            onChange={(v) => setFilters((f) => ({ ...f, due_within_days: v }))}
            options={DUE_WINDOWS}
          />
        </Space>
        <Table
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1600 }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      </Card>
    </div>
  );
};

export default TmmdeEquipmentListPage;
